import { Calendar } from "@fullcalendar/core";
import frLocale from "@fullcalendar/core/locales/fr";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction"; // Pour eventClick
import timeGridPlugin from "@fullcalendar/timegrid";
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    locale: String,
    eventsUrl: String,
    loadingErrorMsg: String,
    loadingDetailsErrorMsg: String,
    academicYearId: String,
    // Emploi du temps : bornes horaires (grille des plages) et, si l'on peut
    // créer, l'URL « Ajouter un créneau » ouverte au clic sur un moment libre.
    slotMinTime: { type: String, default: "08:00:00" },
    slotMaxTime: { type: String, default: "20:00:00" },
    addSlotUrl: String,
    // Date affichée à l'ouverture (dans l'année scolaire choisie).
    initialDate: String,
    // The view a phone (< 768px) opens on, when a page asks for one (e.g. "timeGridDay").
    mobileView: String,
  };
  static targets = ["calendar", "modal", "modalContent"];

  connect() {
    this.initializeCalendar();
  }

  disconnect() {
    if (this.calendar) {
      this.calendar.destroy();
    }
  }

  // On a phone, the page's own view if it asks for one: seven columns of 40px
  // leave the sessions unreadable.
  initialView() {
    const phone = window.matchMedia("(max-width: 767.98px)").matches;
    return phone && this.mobileViewValue ? this.mobileViewValue : "timeGridWeek";
  }

  initializeCalendar() {
    if (!this.hasCalendarTarget) return;

    this.calendar = new Calendar(this.calendarTarget, {
      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
      initialView: this.initialView(),
      ...(this.initialDateValue ? { initialDate: this.initialDateValue } : {}),
      // Agencement aligné sur le calendrier du Design System (.ds-cal) :
      // flèches + titre + "Aujourd'hui" à gauche, sélecteur de vues à droite.
      // Présentation uniquement — n'affecte pas la logique (events, eventClick).
      headerToolbar: {
        left: "prev,next title today",
        center: "",
        right: "dayGridMonth,timeGridWeek,timeGridDay",
      },
      locales: [frLocale],
      locale: this.localeValue,
      height: "auto",
      allDaySlot: false,
      slotMinTime: this.slotMinTimeValue,
      slotMaxTime: this.slotMaxTimeValue,
      slotDuration: "00:30:00",
      eventTimeFormat: {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      },
      events: {
        url: this.eventsUrlValue,
        extraParams: () => {
          const params = {};
          if (this.hasAcademicYearIdValue && this.academicYearIdValue) {
            params["filters[academic_year_id]"] = this.academicYearIdValue;
          }
          return params;
        },
        failure: () => {
          alert(this.loadingErrorMsgValue);
        },
      },
      eventsSet: (events) => this.fitHours(events),
      dateClick: (info) => this.addSlotAt(info),
      eventClick: (info) => {
        this.showSessionDetails(info.event);
        info.jsEvent.preventDefault(); // Empêche le navigateur de suivre le href
      },
    });

    this.calendar.render();
  }

  // Une séance hors des heures affichées (cours avant 8 h ou du soir) serait
  // invisible : la grille s'élargit aux heures des séances chargées.
  fitHours(events) {
    const minutes = (text) => {
      const [hours, mins] = String(text).split(":").map(Number);
      return hours * 60 + (mins || 0);
    };
    const clock = (value) => `${String(Math.floor(value / 60)).padStart(2, "0")}:00:00`;
    this.shownFrom ??= minutes(this.slotMinTimeValue);
    this.shownUntil ??= minutes(this.slotMaxTimeValue);
    let from = this.shownFrom;
    let until = this.shownUntil;
    events.forEach((event) => {
      if (event.allDay || !event.start) return;
      const start = event.start.getHours() * 60 + event.start.getMinutes();
      const end = event.end ? event.end.getHours() * 60 + event.end.getMinutes() : start + 60;
      from = Math.min(from, Math.floor(start / 60) * 60);
      // Une séance qui finit à minuit ou après garde la grille jusqu'à 24 h.
      until = Math.max(until, end > start ? Math.ceil(end / 60) * 60 : 24 * 60);
    });
    if (from === this.shownFrom && until === this.shownUntil) return;

    this.shownFrom = from;
    this.shownUntil = until;
    this.calendar.setOption("slotMinTime", clock(from));
    this.calendar.setOption("slotMaxTime", clock(until));
  }

  // Clic sur un moment libre (vues semaine et jour) : formulaire de créneau
  // prérempli avec le jour et l'heure (en minutes depuis minuit).
  addSlotAt(info) {
    if (!this.hasAddSlotUrlValue || !this.addSlotUrlValue || info.allDay) return;

    const days = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    const date = info.date;
    const url = new URL(this.addSlotUrlValue, window.location.origin);
    url.searchParams.set("day", days[date.getDay()]);
    url.searchParams.set("start", String(date.getHours() * 60 + date.getMinutes()));
    url.searchParams.set("return_to", window.location.pathname + window.location.search);
    if (window.Turbo) {
      window.Turbo.visit(url.toString());
    } else {
      window.location.href = url.toString();
    }
  }

  showSessionDetails(event) {
    const url = event.extendedProps?.detailsUrl || event.url;
    if (!url) {
      return;
    }

    // Fetch turbo stream and inject into DOM — the drawer controller's
    // turbo:before-stream-render listener will auto-open the drawer.
    fetch(url, {
      headers: {
        Accept: "text/vnd.turbo-stream.html, text/html; q=0.9, */*;q=0.8",
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("text/vnd.turbo-stream.html")) {
          return response.text().then((streamHtml) => {
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = streamHtml;
            Array.from(tempDiv.children).forEach((el) => {
              if (el.tagName === "TURBO-STREAM") {
                document.body.appendChild(el);
              }
            });
            return null;
          });
        } else {
          return response.text();
        }
      })
      .then((html) => {
        if (html && this.hasModalContentTarget) {
          this.modalContentTarget.innerHTML = html;
        }
        if (html && this.hasModalTarget) {
          this.modalTarget.classList.remove("hidden");
        }
      })
      .catch((error) => {
        console.error("Error loading session details:", error);
        alert(this.loadingDetailsErrorMsgValue);
      });
  }

  closeModal() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.add("hidden");
    }
  }
}
